<script setup lang="ts">
import type {
  SearchCategoryOption,
  SearchRegionOption,
  SearchServiceOption,
} from "~/utils/search-catalog";

const DEFAULT_SEO_LOCALE = "lt" as const;
const SEO_LOCALES = [
  { code: "lt", language: "lt", ogLocale: "lt_LT" },
  { code: "en", language: "en", ogLocale: "en_US" },
  { code: "ru", language: "ru", ogLocale: "ru_RU" },
] as const;

const runtimeConfig = useRuntimeConfig();
const localeRoute = useLocaleRoute();
const { locale, t } = useI18n();

const { data: catalog } = await useAsyncData("home-catalog", async () => {
  const [categories, services, regions] = await Promise.all([
    $fetch<SearchCategoryOption[]>("/api/categories"),
    $fetch<SearchServiceOption[]>("/api/services"),
    $fetch<SearchRegionOption[]>("/api/regions"),
  ]);

  return { categories, services, regions };
});

const categories = computed(() => catalog.value?.categories || []);
const services = computed(() => catalog.value?.services || []);
const regions = computed(() => catalog.value?.regions || []);

function serializeJsonLd(value: unknown) {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

const siteUrl = String(runtimeConfig.public.siteUrl || "")
  .trim()
  .replace(/\/+$/, "");
const currentLocale = computed(
  () =>
    SEO_LOCALES.find(item => item.code === locale.value) ||
    SEO_LOCALES.find(item => item.code === DEFAULT_SEO_LOCALE) ||
    SEO_LOCALES[0],
);
const seoCopy = computed(() => ({
  title: t("home_page.seo.title"),
  description: t("home_page.seo.description"),
}));
const localizedUrls = computed(() =>
  SEO_LOCALES.map(item => {
    const path = localeRoute("/", item.code)?.path || "/";

    return {
      ...item,
      url: `${siteUrl}${path}`,
    };
  }),
);
const canonicalUrl = computed(
  () =>
    localizedUrls.value.find(item => item.code === currentLocale.value.code)
      ?.url || siteUrl,
);
const defaultUrl = computed(
  () =>
    localizedUrls.value.find(item => item.code === DEFAULT_SEO_LOCALE)?.url ||
    siteUrl,
);
const structuredData = computed(() => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      url: `${siteUrl}/`,
      name: "Senta.lt",
      alternateName: "Senta.lt",
      inLanguage: SEO_LOCALES.map(item => item.language),
    },
    {
      "@type": "WebPage",
      "@id": `${canonicalUrl.value}#webpage`,
      url: canonicalUrl.value,
      name: seoCopy.value.title,
      description: seoCopy.value.description,
      inLanguage: currentLocale.value.language,
      isPartOf: { "@id": `${siteUrl}/#website` },
    },
  ],
}));

useSeoMeta({
  title: () => seoCopy.value.title,
  description: () => seoCopy.value.description,
  robots: "index, follow",
  ogTitle: () => seoCopy.value.title,
  ogDescription: () => seoCopy.value.description,
  ogType: "website",
  ogUrl: () => canonicalUrl.value,
  ogLocale: () => currentLocale.value.ogLocale,
  ogSiteName: "Senta.lt",
});

useHead(() => ({
  htmlAttrs: {
    lang: currentLocale.value.language,
  },
  link: [
    { rel: "canonical", href: canonicalUrl.value },
    ...localizedUrls.value.map(item => ({
      rel: "alternate",
      hreflang: item.language,
      href: item.url,
    })),
    {
      rel: "alternate",
      hreflang: "x-default",
      href: defaultUrl.value,
    },
  ],
  script: [
    {
      type: "application/ld+json",
      key: "home-jsonld",
      innerHTML: serializeJsonLd(structuredData.value),
    },
  ],
}));
</script>

<template>
  <div>
    <HomeHeroSearch :categories="categories" :regions="regions" />
    <HomePopularServices :categories="categories" :services="services" />
    <HomeHowItWorks />
    <HomeJoinBanner />
    <HomeProfessionalsLithuania :categories="categories" :services="services" />
    <HomeGetInTouch />
  </div>
</template>
